/**
 * SectionHeader + SourceDocumentLinks for the structure map.
 */

import { FileText } from "lucide-react";
import { cn } from "@/utils/misc";
import { Badge } from "@/ui/primitives/badge";
import type { SourceDocument } from "../types";

export function SectionHeader({
  icon: Icon,
  color,
  title,
  count,
}: {
  icon: typeof FileText;
  color: string;
  title: string;
  count: number;
}) {
  return (
    <div className="mb-3 flex items-center gap-2">
      <Icon className={cn("h-4 w-4", color)} />
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </span>
      <Badge variant="secondary" size="sm">
        {count}
      </Badge>
    </div>
  );
}

export function SourceDocumentLinks({
  sourceDocuments,
  onDocumentClick,
  className,
}: {
  sourceDocuments?: SourceDocument[];
  onDocumentClick?: (documentId: string) => void;
  className?: string;
}) {
  if (!sourceDocuments || sourceDocuments.length === 0) return null;

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      <FileText className="h-3 w-3 shrink-0 text-muted-foreground/60" />
      {sourceDocuments.map((doc) => (
        <button
          key={doc.id}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDocumentClick?.(doc.id);
          }}
          disabled={!onDocumentClick}
          className={cn(
            "max-w-[180px] truncate rounded border bg-muted/30 px-1.5 py-0.5 text-[10px]",
            onDocumentClick
              ? "cursor-pointer text-primary transition-colors hover:border-primary/30 hover:bg-primary/10"
              : "cursor-default text-muted-foreground",
          )}
          title={doc.fileName}
        >
          {doc.fileName}
        </button>
      ))}
    </div>
  );
}
